import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Workstream } from '../types/workstream';
import ArchiveIcon from './icons/ArchiveIcon';
import UnarchiveIcon from './icons/UnarchiveIcon';

interface WorkstreamActionsProps {
  workstream: Workstream;
  onWorkstreamChanged?: () => void;
}

function PlayIcon({ size = 16 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <polygon points="6,3 20,12 6,21" />
    </svg>
  );
}

function PauseIcon({ size = 16 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <rect x="5" y="3" width="5" height="18" rx="1" />
      <rect x="14" y="3" width="5" height="18" rx="1" />
    </svg>
  );
}

function PlanIcon({ size = 16 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function CopyIcon({ size = 16 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

const btnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 16px',
  fontSize: '13px',
};

export default function WorkstreamActions({ workstream, onWorkstreamChanged }: WorkstreamActionsProps) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsUrl = `/api/workstreams/${encodeURIComponent(workstream.workstreamID)}`;

  const handleStartPause = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const newPaused = !workstream.paused;
    try {
      const resp = await fetch(wsUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Version: workstream.version, Paused: newPaused }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        setError(text || 'Failed to update workstream');
      } else {
        onWorkstreamChanged?.();
      }
    } catch (err: any) {
      setError(err.message || 'Operation failed');
    } finally {
      setBusy(false);
    }
  }, [busy, workstream.paused, workstream.version, wsUrl, onWorkstreamChanged]);

  const handleArchiveToggle = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const isDeleted = workstream.deleted;
    try {
      const url = isDeleted ? `${wsUrl}/unarchive` : wsUrl;
      const method = isDeleted ? 'POST' : 'DELETE';
      const resp = await fetch(url, { method });
      if (!resp.ok && resp.status !== 204) {
        const text = await resp.text();
        setError(text || `Failed to ${isDeleted ? 'unarchive' : 'archive'} workstream`);
      } else {
        onWorkstreamChanged?.();
      }
    } catch (err: any) {
      setError(err.message || 'Operation failed');
    } finally {
      setBusy(false);
    }
  }, [busy, workstream.deleted, wsUrl, onWorkstreamChanged]);

  const handleClone = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(`${wsUrl}/clone`, { method: 'POST' });
      if (!resp.ok) {
        const text = await resp.text();
        setError(text || 'Failed to clone workstream');
      } else {
        const data: Workstream = await resp.json();
        navigate(`/workstreams/${encodeURIComponent(data.workstreamID)}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to clone workstream');
    } finally {
      setBusy(false);
    }
  }, [busy, wsUrl, navigate]);

  const isPaused = workstream.paused;
  const isDeleted = workstream.deleted;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {/* Start / Pause */}
        {!isDeleted && (
          <button
            type="button"
            disabled={busy}
            onClick={handleStartPause}
            style={{
              ...btnStyle,
              '--button-bg': isPaused ? '#28a745' : '#ffc107',
              '--button-bg-hover': isPaused ? '#22863a' : '#e0a800',
              '--button-bg-active': isPaused ? '#1e7e34' : '#d39e00',
              '--button-color': isPaused ? '#fff' : '#24292f',
              '--button-color-hover': isPaused ? '#fff' : '#24292f',
            } as React.CSSProperties}
          >
            {isPaused ? <PlayIcon /> : <PauseIcon />}
            {isPaused ? 'Start' : 'Pause'}
          </button>
        )}

        {/* Archive / Unarchive */}
        <button
          type="button"
          className={isDeleted ? undefined : 'btn-danger'}
          disabled={busy}
          onClick={handleArchiveToggle}
          style={btnStyle}
        >
          {isDeleted ? <UnarchiveIcon size={16} /> : <ArchiveIcon size={16} />}
          {isDeleted ? 'Unarchive' : 'Archive'}
        </button>

        {/* Clone */}
        {!isDeleted && (
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={handleClone}
            style={btnStyle}
          >
            <CopyIcon />
            Clone
          </button>
        )}

        {/* Plan */}
        {!isDeleted && (
          <button
            type="button"
            disabled={busy}
            onClick={() => navigate('/tasks/new')}
            style={btnStyle}
          >
            <PlanIcon />
            Plan
          </button>
        )}
      </div>

      {error && (
        <div
          className="error-banner"
          style={{
            margin: '8px 0 0',
            padding: '8px 12px',
            background: '#ffebe9',
            border: '1px solid #cf222e',
            borderRadius: '6px',
            color: '#cf222e',
            fontSize: '13px',
          }}
        >
          {error}
          <button type="button" className="error-banner__close" onClick={() => setError(null)}>
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
